// Default resolution tiers (px) — the dimensions we request from Cloudimage when the
// configuration tool ("global" config) does not provide an override. Every width/height
// request is snapped to the nearest tier so the CDN serves a single shared cached variant
// across users and devices, instead of a different (separately cached) variant per size.
export const CLOUDIMAGE_RESOLUTIONS = {
  low: 640,
  medium: 800,
  high: 1024,
} as const;

export type CloudImageResolution = keyof typeof CLOUDIMAGE_RESOLUTIONS;

// Tier overrides loaded from the configuration tool into the `cloudImageTiers` redux slice
// (see fetchCloudImageTiers / CloudImageTiersThunks). Any tier left unset falls back to its
// hardcoded CLOUDIMAGE_RESOLUTIONS default.
export interface CloudImageTiers {
  low?: number;
  medium?: number;
  high?: number;
}

// Cloudimage v7 serves transformations from `{base}/v7/{source-url}?{operators}`.
const V7_SEGMENT = "v7";

// Resolve one tier dimension: the configured override when it is a positive number, otherwise
// the hardcoded default. Tolerates string values (the config tool may serialize numbers as text).
const tierDimension = (
  value: number | string | undefined,
  fallback: number
): number => {
  const parsed = typeof value === "string" ? parseInt(value, 10) : value;
  return typeof parsed === "number" && parsed > 0 ? parsed : fallback;
};

// Resolve the active tier dimensions (ascending): config-tool overrides (from the redux store)
// over the hardcoded defaults. Reads the store at call time; defaults apply until it is loaded.
const tierDimensions = (): number[] => {
  // Require the store lazily (instead of importing it at module top): this util is pulled in
  // very early through the `utils` barrel, and a static `import` of the store would force the
  // whole redux store/rootReducer to initialize before the reducers are registered — leaving
  // slices such as `mobileState`/`brandTheme` undefined. By the time getCloudImageUrl actually
  // runs (render time) the store is fully built. `typeof import` keeps the typing, erased at runtime.
  const { store } = require("../redux/store") as typeof import("../redux/store");
  const tiers: CloudImageTiers = store.getState().cloudImageTiers?.tiers ?? {};
  return [
    tierDimension(tiers.low, CLOUDIMAGE_RESOLUTIONS.low),
    tierDimension(tiers.medium, CLOUDIMAGE_RESOLUTIONS.medium),
    tierDimension(tiers.high, CLOUDIMAGE_RESOLUTIONS.high),
  ].sort((a, b) => a - b);
};

// Snap a requested size to the nearest tier dimension. Requests below the smallest tier
// floor to it (low), requests above the largest cap to it (high), and in-between sizes go to
// whichever tier they are closest to — on an exact midpoint the smaller tier wins (less
// transfer). This keeps the requested dimension on the fixed {low, medium, high} grid so the
// CDN cache is shared instead of fragmented across arbitrary sizes.
const snapToTier = (requested: number): number =>
  tierDimensions().reduce((best, tier) =>
    Math.abs(tier - requested) < Math.abs(best - requested) ? tier : best
  );

// Snap `width`/`height` requests to a tier. Only plain integer sizes are snapped; composite
// sizes (e.g. "300x200" used by crop/fit/cover operations) are passed through untouched.
const snapSize = (operation?: string, size?: string): string | undefined => {
  if (
    !size ||
    (operation !== "width" && operation !== "height") ||
    !/^\d+$/.test(size)
  ) {
    return size;
  }

  return String(snapToTier(parseInt(size, 10)));
};

// Build the Cloudimage v7 query string from the legacy (operation, size, filter) triple.
// v7 carries the operators as query params instead of v6's `/operation/size/filter/` path:
//   width/400          -> width=400
//   height/200         -> height=200
//   <func>/300x200     -> w=300&h=200&func=<func>   (crop, fit, bound, cover, ...)
//   q80 (filter)       -> q=80
const buildQuery = (
  operation?: string,
  size?: string,
  filter?: string
): string => {
  const parts: string[] = [];
  const snappedSize = snapSize(operation, size);

  if (operation && snappedSize) {
    const composite = snappedSize.match(/^(\d+)x(\d+)$/);
    if (composite) {
      parts.push(`w=${composite[1]}`, `h=${composite[2]}`, `func=${operation}`);
    } else {
      // Single-dimension operation (width / height). Pass any other operation through verbatim.
      parts.push(`${operation}=${snappedSize}`);
    }
  }

  if (filter) {
    // v6 quality filter `q80` -> v7 `q=80`; forward any other filter token unchanged.
    const quality = filter.match(/^q(\d+)$/);
    parts.push(quality ? `q=${quality[1]}` : filter);
  }

  return parts.join("&");
};

// Add the `/v7/` segment to the configured base url (idempotently, in case it is ever moved
// into CLOUDIMAGE_BASE_URL itself) without disturbing the trailing source url.
const withV7Segment = (base: string): string => {
  const trimmed = base.replace(/\/+$/, "");
  return /\/v7$/i.test(trimmed) ? trimmed : `${trimmed}/${V7_SEGMENT}`;
};

// Force WebP delivery regardless of the client's `Accept` header. The account-level "auto
// WebP" only kicks in when the client advertises `Accept: image/webp`, which native mobile
// image loaders typically do not — so we ask for WebP explicitly on every request.
const FORCE_WEBP = "force_format=webp";

// A GIF source is left untouched: forcing WebP would flatten animated GIFs (and the
// account-level "animated GIF -> WebP" conversion is intentionally left disabled).
const isGif = (url: string): boolean => /\.gif(\?|#|$)/i.test(url);

export const getCloudImageUrl = (
  // tslint:disable-next-line: no-any
  url: any,
  operation?: string,
  size?: string,
  filter?: string
): string => {
  const query = [
    buildQuery(operation, size, filter),
    isGif(String(url)) ? "" : FORCE_WEBP,
  ]
    .filter(Boolean)
    .join("&");
  const path = `${withV7Segment(window.config.CLOUDIMAGE_BASE_URL)}/${url}`;

  return query ? `${path}?${query}` : path;
};

export default getCloudImageUrl;
